import axios from 'axios';
import logmirror from './logmirror';

function randomOrderID() {
    return "ord_" + (Date.now().toString(36) + "_" + Math.random().toString(36).substr(-5)).toLowerCase();
}

function build(restaurant, order, transaction) {
    const payload = {
        "restaurant": {
            "id": restaurant.id,
            "name": restaurant.name,
            "phone": restaurant.phone_number,
            "token": restaurant.token,
        },
        "order": {
            "amount": order.amount,
            "commissions": order.commissions,
            "coupon": order.coupon,
            "customer": order.customer,
            "customers": order.customers,
            "id": randomOrderID(),
            "number": order.number,
            "payed": order.payed,
            "products": order.products,
            "quantity": order.quantity,
            "table": order.table,
            "timestamp": order.timestamp,
            "total": order.total,
            "type": order.type,
            "note": order.note,
        },
        "transaction": {
            "id": transaction.id,
            "provider": transaction.provider,
        },
    };

    return payload;
}

const PATH = "https://orders.mycontactlessmenu.mycia.it/api/webapp/orders";
const TILLB = "https://api.mycia.it/v1/businesses/billd";
const OUTBOX_KEY = "__mycontactlessmenu_outbox__";

// An order delivered later than this must not reach the kitchen as fresh:
// same-meal window, reconciled manually via telemetry beyond it.
const OUTBOX_TTL = 60 * 60 * 1000;

function readOutbox() {
    try {
        const queue = JSON.parse(window.localStorage.getItem(OUTBOX_KEY));
        return Array.isArray(queue) ? queue : [];
    } catch (err) {
        return [];
    }
}

function writeOutbox(queue) {
    try {
        window.localStorage.setItem(OUTBOX_KEY, JSON.stringify(queue));
        return true;
    } catch (err) {
        console.error(err);
        return false;
    }
}

// One row per order event on the sheet; the payment id joins it to Stripe.
function mirror(entry, action, extra) {
    const { restaurant, order, transaction } = entry.data;

    logmirror.mirrorLog(restaurant.id, action, {
        "order_id": order && order.id,
        "pi": transaction && transaction.id,
        "provider": transaction && transaction.provider,
        ...extra,
    });
}

async function sendEntry(entry) {
    try {
        await axios.post(PATH, { data: entry.data });
    } catch (err) {
        console.error(err);
        mirror(entry, "order_post_failed", { "error": err && err.message });
        return false;
    }

    // Mirrored here and not on the earlier "sendorder" tracking: this is the
    // moment the dashboard actually received the order.
    const { order } = entry.data;
    mirror(entry, "order_sent", { "amount": order && order.amount, "order_type": order && order.type });

    if (entry.hasBilld) {
        await axios.post(TILLB, { data: entry.data }).catch(console.error);
    }

    return true;
}

// The customer already paid at this point: the order is persisted first and
// only removed once delivered, so a failed POST can be retried later.
async function doPost(data, hasBilld) {
    const entry = { data, hasBilld, ts: Date.now() };
    const queue = readOutbox();
    queue.push(entry);

    if (!writeOutbox(queue)) {
        // Storage broken: single attempt, like the original code.
        await sendEntry(entry);
        return;
    }

    await flushOutbox();
}

// Flushes are chained, never concurrent: a parallel flush could overwrite
// the queue and silently drop an order enqueued meanwhile.
let flushChain = Promise.resolve();

function flushOutbox() {
    flushChain = flushChain.then(flushOnce).catch(console.error);
    return flushChain;
}

async function flushOnce() {
    const queue = readOutbox();
    if (queue.length === 0) {
        return;
    }

    const failed = [];
    for (const entry of queue) {
        if (!entry.ts || Date.now() - entry.ts > OUTBOX_TTL) {
            mirror(entry, "order_post_expired");
            continue;
        }
        if (!(await sendEntry(entry))) {
            failed.push(entry);
        }
    }

    // Keep anything enqueued while we were sending.
    writeOutbox(failed.concat(readOutbox().slice(queue.length)));
}

// The preview key expires shortly after issue: fetch it right before opening.
async function getBilldPreviewUrl(orderId) {
    const res = await axios.get(`${TILLB}/${orderId}/preview`);
    return res.data.previewUrl;
}

const checkoutClient = {
    randomOrderID,
    build,
    doPost,
    flushOutbox,
    getBilldPreviewUrl,
};
export default checkoutClient;
